export interface FieldDescriptor {
  formIndex?: number;
  name: string;
  label?: string;
  type: string;
  placeholder?: string;
  required?: boolean;
  isPrivate?: boolean;
  options?: { value: string; label: string; name?: string }[];
  value?: string | number | boolean | string[] | undefined;
  element?: HTMLElement; // Reference to actual DOM element for fields with fallback names
  tagName?: string; // Element tagName for format detection (e.g., 'ion-datetime', 'gc-input')
}

export class FormScanner {
  private form: HTMLFormElement;

  constructor(form: HTMLFormElement) {
    this.form = form;
  }

  private static isTranslationKey(text: string | null | undefined): boolean {
    if (!text) return false;
    const trimmedText = text.trim();
    // Check for {{ ... }} or | translate
    if (/\{\{.*\}\}|\|\s*translate/.test(trimmedText)) {
      return true;
    }
    // Check for pattern like some.key.name or SOME_KEY_NAME
    // Allows single words, but flags dot-separated or underscore_separated typical keys
    if (/^([a-zA-Z0-9]+[._-])+[a-zA-Z0-9]+$/.test(trimmedText) && (trimmedText.includes('.') || trimmedText.includes('_') || trimmedText.includes('-'))) {
        // Further check: if it's very short and could be a legitimate multi-word label with a dot/hyphen by mistake.
        // Example: "Version 1.0" should not be a key. "user.profile.save" should be.
        if (trimmedText.length > 30 || trimmedText.split(/[._-]/).length > 2) return true;
    }
    // Check if it's a common placeholder key pattern (often lowercase with hyphens)
    if (/^[a-z0-9-]+-[a-z0-9-]+/.test(trimmedText) && trimmedText.length > 20) { // e.g. project_name_placeholder
        return true;
    }
    return false;
  }

  public isLikelyDisplayOnlyField(element: HTMLElement, value: any, _label: string): boolean {
    // Check if it's in a table with many similar elements (likely a data table)
    const table = element.closest('table');
    if (table) {
      // Count similar elements in the table
      const similarElements = table.querySelectorAll(element.tagName).length;
      if (similarElements > 5) {
        return true; // Likely a data table, not a form
      }
    }
    
    // Check if value is just a number (but not empty string)
    if (value !== '' && typeof value === 'string' && /^\d+$/.test(value) && value.length <= 2) {
      // Especially if there are many similar elements nearby
      const parent = element.parentElement?.parentElement;
      if (parent) {
        const siblings = parent.querySelectorAll(element.tagName);
        if (siblings.length > 3) {
          return true; // Likely a display grid
        }
      }
    }
    
    // Check if element is in a list with many similar items
    if (element.closest('ul, ol') && !element.hasAttribute('contenteditable')) {
      return true;
    }
    
    // Check for common display-only patterns
    const className = element.className.toString().toLowerCase();
    if (className.includes('readonly') || 
        className.includes('display') ||
        className.includes('static') ||
        className.includes('label') ||
        className.includes('view') ||
        className.includes('text') && !className.includes('textbox') && !className.includes('textarea')) {
      return true;
    }
    
    // Check if it's a custom element that doesn't have input indicators
    const tagName = element.tagName.toLowerCase();
    if ((tagName.includes('field') || tagName.includes('input') || tagName.includes('search')) && 
        !element.querySelector('input, select, textarea') &&
        !element.hasAttribute('contenteditable') &&
        !element.hasAttribute('ng-model') &&
        !element.hasAttribute('v-model') &&
        !element.hasAttribute('formcontrolname')) {
      // Custom element without any real input indicators
      return true;
    }
    
    return false;
  }
  
  /**
   * Проверяет, является ли элемент внутренним полем составного компонента
   * Такие поля не должны быть доступны для прямого заполнения
   */
  private isInternalFieldOfCompositeComponent(element: HTMLElement): boolean {
    // Список составных компонентов, внутренние поля которых нужно игнорировать
    const compositeComponentSelectors = [
      'app-input-interval',     // Retention field component
      'app-date-range',         // Date range components
      'app-time-range',         // Time range components  
      'app-number-range',       // Number range components
      'cloud-ui-input-tags',    // Multi-select tag inputs
      '[class*="composite"]',   // Any element with "composite" in class
      '[class*="interval"]',    // Any element with "interval" in class
      '[class*="range"]'        // Any element with "range" in class
    ];
    
    // Проверяем, находится ли элемент внутри составного компонента
    for (const selector of compositeComponentSelectors) {
      const compositeParent = element.closest(selector);
      if (compositeParent && compositeParent !== element) {
        return true;
      }
    }
    
    return false;
  }
  
  public isInteractiveElement(element: HTMLElement): boolean {
    // Check if it's a native form element
    if (element instanceof HTMLInputElement || 
        element instanceof HTMLSelectElement || 
        element instanceof HTMLTextAreaElement) {
      // Additional check for read-only or display-only inputs
      if (element.hasAttribute('readonly') || 
          element.getAttribute('aria-readonly') === 'true') {
        return false;
      }
      return true;
    }

    // Check ARIA roles
    const role = element.getAttribute('role');
    if (role && ['textbox', 'combobox', 'searchbox', 'spinbutton'].includes(role)) {
      return true;
    }

    // Check contenteditable
    if (element.getAttribute('contenteditable') === 'true') {
      return true;
    }

    // Check if element is in a table cell (likely display-only)
    if (element.closest('td, th')) {
      // Unless it has explicit input indicators
      if (!element.hasAttribute('formcontrolname') && 
          !element.hasAttribute('ng-model') &&
          !element.hasAttribute('v-model')) {
        return false;
      }
    }

    // Check for custom components with input-like behavior
    // Look for nested native inputs
    const hasNestedInput = element.querySelector('input:not([readonly]), select:not([readonly]), textarea:not([readonly])');
    if (hasNestedInput) {
      return true;
    }

    // Check for common interactive attributes
    if (element.hasAttribute('ng-model') || 
        element.hasAttribute('v-model') || 
        element.hasAttribute('formcontrolname') ||
        element.hasAttribute('data-bind')) {
      return true;
    }

    // Check if element has tabindex (indicating it's focusable)
    const tabIndex = element.getAttribute('tabindex');
    if (tabIndex && parseInt(tabIndex) >= 0) {
      // But not if it's in a table or has aria-readonly
      if (element.closest('table') || element.getAttribute('aria-readonly') === 'true') {
        return false;
      }
      return true;
    }

    return false;
  }

  private findDeepNestedInput(element: HTMLElement): HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null {
    // If element itself is an input
    if (element instanceof HTMLInputElement || 
        element instanceof HTMLSelectElement || 
        element instanceof HTMLTextAreaElement) {
      return element;
    }
    
    // Search deeply for any input element
    const inputs = element.querySelectorAll('input:not([type="hidden"]), select, textarea');
    if (inputs.length > 0) {
      return inputs[0] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    }
    
    // Check Shadow DOM if available
    if (element.shadowRoot) {
      const shadowInputs = element.shadowRoot.querySelectorAll('input:not([type="hidden"]), select, textarea');
      if (shadowInputs.length > 0) {
        return shadowInputs[0] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      }
    }
    
    return null;
  }

  public extractValue(element: HTMLElement): string | string[] | boolean | undefined {
    // First try to find deeply nested input
    const deepInput = this.findDeepNestedInput(element);
    if (deepInput) {
      element = deepInput; // Use the nested input for value extraction
    }
    
    // Native form elements
    if (element instanceof HTMLInputElement) {
      if (element.type === 'checkbox') {
        return element.checked ? (element.value || 'on') : undefined;
      }
      return element.value;
    }
    if (element instanceof HTMLSelectElement) {
      return element.value;
    }
    if (element instanceof HTMLTextAreaElement) {
      return element.value;
    }

    // Contenteditable
    if (element.getAttribute('contenteditable') === 'true') {
      return element.textContent?.trim() || '';
    }

    // Check for nested native input
    const nestedInput = element.querySelector('input, select, textarea') as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
    if (nestedInput) {
      if (nestedInput instanceof HTMLInputElement && nestedInput.type === 'checkbox') {
        return nestedInput.checked ? (nestedInput.value || 'on') : undefined;
      }
      return nestedInput.value;
    }

    // Check for value attribute
    if (element.hasAttribute('value')) {
      return element.getAttribute('value') || '';
    }

    // Check for Angular/Vue/React bindings
    const ngModel = element.getAttribute('ng-model');
    const vModel = element.getAttribute('v-model');
    if (ngModel || vModel) {
      // Try to get value from data attributes or text content
      return element.getAttribute('data-value') || element.textContent?.trim() || '';
    }

    // Check for aria-valuenow (for spinbuttons)
    const ariaValue = element.getAttribute('aria-valuenow');
    if (ariaValue) {
      return ariaValue;
    }

    // Last resort: check text content for simple custom components
    return element.textContent?.trim() || '';
  }

  public getElementType(element: HTMLElement): string {
    // First try to find deeply nested input
    const deepInput = this.findDeepNestedInput(element);
    if (deepInput) {
      if (deepInput instanceof HTMLInputElement) {
        return deepInput.type || 'text';
      }
      if (deepInput instanceof HTMLSelectElement) {
        return deepInput.multiple ? 'select-multiple' : 'select-one';
      }
      if (deepInput instanceof HTMLTextAreaElement) {
        return 'textarea';
      }
    }
    
    // Native elements
    if (element instanceof HTMLInputElement) {
      return element.type || 'text';
    }
    if (element instanceof HTMLSelectElement) {
      return element.multiple ? 'select-multiple' : 'select-one';
    }
    if (element instanceof HTMLTextAreaElement) {
      return 'textarea';
    }

    // Check role attribute
    const role = element.getAttribute('role');
    if (role) {
      const roleToType: Record<string, string> = {
        'textbox': 'text',
        'combobox': 'select-one',
        'searchbox': 'search',
        'spinbutton': 'number'
      };
      if (roleToType[role]) {
        return roleToType[role];
      }
    }

    // Check data attributes
    const dataType = element.getAttribute('data-type') || element.getAttribute('data-input-type');
    if (dataType) {
      return dataType;
    }

    // Check tag name for hints
    const tagName = element.tagName.toLowerCase();
    if (tagName.includes('search')) return 'search';
    if (tagName.includes('email')) return 'email';
    if (tagName.includes('password')) return 'password';
    if (tagName.includes('select')) return 'select-one';
    if (tagName.includes('textarea')) return 'textarea';

    // Default to text
    return 'text';
  }

  public getElementName(element: HTMLElement): string {
    // Try standard name attribute
    let name = element.getAttribute('name');
    if (name) return name;

    // Try id
    name = element.id;
    if (name) return name;

    // Check framework bindings (BEFORE checking nested inputs to avoid duplicates)
    name = element.getAttribute('formcontrolname') || // Angular reactive forms
           element.getAttribute('ng-model') || 
           element.getAttribute('v-model') || 
           element.getAttribute('data-field-name') ||
           element.getAttribute('data-name') ||
           element.getAttribute('data-testid') ||
           element.getAttribute('data-cy') ||
           element.getAttribute('data-field') ||
           element.getAttribute('aria-labelledby');
    if (name) return name;

    // Check for deeply nested input name
    const deepInput = this.findDeepNestedInput(element);
    if (deepInput) {
      name = deepInput.getAttribute('name') || deepInput.id;
      if (name) return name;
    }

    // Generate fallback name based on element type or placeholder
    const placeholder = element.getAttribute('placeholder') || 
                       deepInput?.getAttribute('placeholder');
    if (placeholder) {
      // Special handling for time patterns (00:00 -> time00, HH:MM -> timeHHMM)
      if (/^\d{1,2}:\d{2}$/.test(placeholder.trim())) {
        const timeValue = placeholder.replace(/[^0-9]/g, '');
        return `time${timeValue}`;
      }
      
      // Create name from placeholder (e.g., "Search for resources" -> "searchForResources")
      let baseName = placeholder.toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .split(' ')
        .map((word, i) => i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      
      // Ensure name starts with letter (CSS selector requirement)
      if (/^\d/.test(baseName)) {
        baseName = `field${baseName}`;
      }
      
      return baseName;
    }

    // Try to generate name from label text
    const labelText = this.findLabelText(element);
    if (labelText && !FormScanner.isTranslationKey(labelText)) {
      let baseName = labelText.toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .split(' ')
        .map((word, i) => i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      
      // Ensure name starts with letter (CSS selector requirement)
      if (/^\d/.test(baseName)) {
        baseName = `field${baseName}`;
      }
      
      // Only return if we got a meaningful name
      if (baseName && baseName.length > 0) {
        return baseName;
      }
    }

    // No fallback naming - return empty string for fields without identifiable names
    return '';
  }

  public static scanAllFormsOnPage(): FieldDescriptor[] {
    const fields: FieldDescriptor[] = [];
    const processedElements = new Set<HTMLElement>();
    
    console.log('[FormScanner] Starting scanAllFormsOnPage...');
    console.log('[FormScanner] Traditional forms found:', document.forms.length);
    
    // Scan traditional forms
    Array.from(document.forms).forEach((form, index) => {
      const formFields = new FormScanner(form).scan().map(field => ({ ...field, formIndex: index }));
      console.log(`[FormScanner] Form ${index} found ${formFields.length} fields`);
      fields.push(...formFields);
      
      // Mark all elements in this form as processed
      form.querySelectorAll('*').forEach(el => processedElements.add(el as HTMLElement));
    });

    // Find ALL elements that might be form containers
    const allFormLikeElements = Array.from(document.querySelectorAll('*')).filter(el => {
      const tagName = el.tagName.toLowerCase();
      const className = el.className.toString().toLowerCase();
      const id = el.id?.toLowerCase() || '';
      
      // Check if element has form-like attributes
      return (tagName.includes('form') || 
              className.includes('form') || 
              className.includes('card') || // Common container for form sections
              className.includes('panel') || // Another common container
              className.includes('section') ||
              id.includes('form') ||
              el.getAttribute('role') === 'form' ||
              el.hasAttribute('data-form')) &&
             tagName !== 'form' && // Exclude actual <form> tags
             !el.closest('table'); // Exclude elements inside tables
    });
    
    console.log('[FormScanner] Form-like containers found:', allFormLikeElements.length);
    
    allFormLikeElements.forEach((container, index) => {
      // Skip if it's inside a real form
      if (container.closest('form')) return;
      
      // Create a fake form element for scanning
      const fakeForm = document.createElement('form');
      // Copy all children temporarily (we'll use cloneNode to avoid DOM manipulation)
      container.childNodes.forEach(child => {
        fakeForm.appendChild(child.cloneNode(true));
      });
      
      const containerFields = new FormScanner(fakeForm as HTMLFormElement).scan()
        .map(field => ({ ...field, formIndex: document.forms.length + index }));
      
      if (containerFields.length > 0) {
        console.log(`[FormScanner] Form-like container ${index} found ${containerFields.length} fields`);
        fields.push(...containerFields);
      }
    });

    // Scan for ALL interactive elements on the page
    const interactiveSelectors = [
      // Native form elements
      'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="image"]):not([type="reset"])',
      'select',
      'textarea',
      // ARIA roles
      '[role="textbox"]',
      '[role="combobox"]',
      '[role="searchbox"]',
      '[role="spinbutton"]',
      // Contenteditable
      '[contenteditable="true"]',
      // Framework attributes
      '[formcontrolname]',
      '[ng-model]',
      '[v-model]',
      // Custom components that might contain inputs
      'gc-input',
      'gc-select',
      'gc-textarea',
      'gc-search-input',
      'app-input',
      'custom-input',
      '*[class*="input"]:not(input)',
      '*[class*="select"]:not(select)',
      '*[class*="search"]:not(input)',
      '*[class*="field"]:not(fieldset)',
      '*[class*="form-control"]',
      '*[class*="form-input"]',
      // Data attributes that might indicate input fields
      '[data-testid*="field"]',
      '[data-testid*="input"]',
      '[data-cy*="field"]',
      '[data-cy*="input"]',
      '[data-field]',
      '[aria-labelledby]',
      // Common input wrapper patterns
      '.input-wrapper input',
      '.field-wrapper input',
      '.form-field input',
      '.form-control input'
    ];
    
    // Find ALL interactive elements on the page
    console.log('[FormScanner] Searching for ALL interactive elements...');
    const allInteractiveElements = document.querySelectorAll(interactiveSelectors.join(', '));
    console.log('[FormScanner] Found', allInteractiveElements.length, 'potential interactive elements');
    
    // Process all interactive elements
    console.log('[FormScanner] Processing interactive elements...');
    const standaloneFieldsMap = new Map<string, FieldDescriptor>();
    
    allInteractiveElements.forEach(element => {
      if (!element || processedElements.has(element as HTMLElement)) return;
      
      const el = element as HTMLElement;
      const scanner = new FormScanner(document.createElement('form')); // Dummy form for methods
      
      // Get element details
      const deepInput = scanner.findDeepNestedInput(el);
      const name = scanner.getElementName(el);
      
      // Skip fields without identifiable names
      if (!name) {
        console.log(`[FormScanner] Skipping standalone field without name/id:`, el);
        return;
      }
      
      const type = scanner.getElementType(el);
      const label = scanner.getHumanReadableLabel(el, name);
      const value = scanner.extractValue(el);
      
      // Check if it's interactive
      if (!scanner.isInteractiveElement(el)) {
        return;
      }
      
      // Check visibility
      if (el.offsetParent === null && !el.checkVisibility?.()) {
        return;
      }
      
      // Check if disabled or readonly
      if (el.hasAttribute('disabled') || 
          el.getAttribute('aria-disabled') === 'true' ||
          el.hasAttribute('readonly') ||
          el.getAttribute('aria-readonly') === 'true') {
        return;
      }
      
      // Skip if element has suspicious patterns
      // Skip numeric-only names or values that look like data display
      if ((name === '0' || name.match(/^\d+$/)) && 
          (value === '0' || value === '' || (typeof value === 'string' && value.match(/^\d+$/)))) {
        console.log(`[FormScanner] Skipping likely display-only element:`, el.tagName, `name="${name}"`, `value="${value}"`);
        return;
      }
      
      // Skip if element with numeric value is in a table
      if (value === '0' && el.closest('table')) {
        console.log(`[FormScanner] Skipping likely display-only element in table:`, el.tagName, name);
        return;
      }
      
      const required = el.hasAttribute('required') || el.getAttribute('aria-required') === 'true';
      const isPrivate = el.dataset.aiPrivate === 'true' || 
                       el.getAttribute('data-private') === 'true' ||
                       type === 'password' || 
                       /cc-|credit.*card|card.*number|cvv|cvc|exp.*date|expir/i.test(name) ||
                       /password|pwd|secret/i.test(name);
      
      let placeholder: string | undefined;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        placeholder = el.placeholder;
      } else {
        placeholder = el.getAttribute('placeholder') || 
                     el.getAttribute('aria-placeholder') || 
                     el.getAttribute('ng-reflect-placeholder') ||
                     undefined;
      }
      
      // Additional filtering for likely non-input fields
      // Skip this check for fields with meaningful placeholders
      const hasPlaceholder = el.getAttribute('placeholder') || 
                           (el instanceof HTMLInputElement && el.placeholder) ||
                           deepInput?.getAttribute('placeholder');
      
      if (!hasPlaceholder && scanner.isLikelyDisplayOnlyField(el, value, label)) {
        console.log(`[FormScanner] Skipping likely display-only field:`, el.tagName, name, value);
        return;
      }
      
      const fieldDescriptor: FieldDescriptor = {
        name,
        label,
        type,
        placeholder,
        required,
        isPrivate,
        value,
        formIndex: -1,
        element: el // Всегда сохраняем элемент для возможности подсветки
      };
      
      // Avoid duplicates
      if (!standaloneFieldsMap.has(name)) {
        standaloneFieldsMap.set(name, fieldDescriptor);
        console.log(`[FormScanner] Found standalone field: "${name}" (${type}) - "${label}"`);
      }
      
      processedElements.add(el);
    });
    
    const standaloneFields = Array.from(standaloneFieldsMap.values());
    if (standaloneFields.length > 0) {
      console.log('[FormScanner] Standalone fields detected:', standaloneFields.length);
      standaloneFields.forEach(field => {
        console.log(`  - ${field.name}: "${field.label}" (${field.type})`);
      });
    }
    fields.push(...standaloneFields);

    console.log('[FormScanner] === SUMMARY: Total fields found:', fields.length, '===');
    fields.forEach((field, index) => {
      const formInfo = field.formIndex !== undefined && field.formIndex >= 0 ? ` [Form ${field.formIndex}]` : ' [Standalone]';
      console.log(`  ${index + 1}. ${field.name}: "${field.label}" (${field.type})${formInfo}`);
    });
    return fields;
  }

  public getHumanReadableLabel(element: HTMLElement, baseNameOrId: string): string {
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel && !FormScanner.isTranslationKey(ariaLabel)) {
      return ariaLabel.trim();
    }

    // Check for deeply nested input's placeholder
    const deepInput = this.findDeepNestedInput(element);
    if (deepInput && deepInput instanceof HTMLInputElement || deepInput instanceof HTMLTextAreaElement) {
      const deepPlaceholder = deepInput.placeholder;
      if (deepPlaceholder && !FormScanner.isTranslationKey(deepPlaceholder)) {
        return deepPlaceholder.trim();
      }
    }

    const placeholder = (element as HTMLInputElement | HTMLTextAreaElement).placeholder ||
                       element.getAttribute('placeholder') ||
                       element.getAttribute('aria-placeholder') ||
                       element.getAttribute('ng-reflect-placeholder'); // Angular reflection
    if (placeholder && !FormScanner.isTranslationKey(placeholder)) {
      return placeholder.trim();
    }

    const labelTextFromDOM = this.findLabelText(element); // This now includes looking for previous siblings
    if (labelTextFromDOM && !FormScanner.isTranslationKey(labelTextFromDOM)) {
      return labelTextFromDOM.trim().replace(/\*$/, '').trim();
    }
    
    // Try to make the name more human readable
    if (baseNameOrId && !baseNameOrId.startsWith('field_')) {
      // Convert camelCase or snake_case to readable format
      const readable = baseNameOrId
        .replace(/([A-Z])/g, ' $1') // camelCase to spaces
        .replace(/_/g, ' ') // snake_case to spaces
        .replace(/\s+/g, ' ') // multiple spaces to single
        .trim()
        .toLowerCase()
        .replace(/^\w/, c => c.toUpperCase()); // Capitalize first letter
      return readable;
    }
    
    // Fallback to name or ID
    return baseNameOrId.trim();
  }

  public scan(): FieldDescriptor[] {
    // Universal selectors based on best practices from autofill algorithms
    const universalSelectors = [
      // Native form elements
      'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="image"]):not([type="reset"])',
      'select',
      'textarea',
      // ARIA roles
      '[role="textbox"]',
      '[role="combobox"]',
      '[role="searchbox"]',
      '[role="spinbutton"]',
      // Contenteditable elements
      '[contenteditable="true"]',
      // Common custom component patterns
      '[data-input]',
      '[data-form-field]',
      '[data-field]',
      // Framework-specific attributes
      '[ng-model]',
      '[v-model]',
      '[formcontrolname]',
      '[data-bind]'
    ];
    
    // Additionally, find custom elements that contain input-like keywords in their tag names
    const customElementSelectors = [
      '*[class*="input"]:not(input):not([type="hidden"])',
      '*[class*="select"]:not(select)',
      '*[class*="textarea"]:not(textarea)',
      '*[class*="dropdown"]:not(select)',
      '*[class*="datepicker"]:not(input)',
      '*[class*="field"]:not(fieldset)'
    ];
    
    // Find all elements with tag names containing input keywords
    const allPotentialElements = [
      ...Array.from(this.form.querySelectorAll(universalSelectors.join(', '))),
      ...Array.from(this.form.querySelectorAll(customElementSelectors.join(', '))),
      ...Array.from(this.form.querySelectorAll('*')).filter(el => {
        const tagName = el.tagName.toLowerCase();
        // Check if tag name contains any input-related keywords
        return (tagName.includes('input') || 
                tagName.includes('select') || 
                tagName.includes('textarea') || 
                tagName.includes('dropdown') ||
                tagName.includes('picker') ||
                tagName.includes('search') ||
                tagName.includes('field')) &&
                !['fieldset', 'input', 'select', 'textarea'].includes(tagName);
      })
    ];

    // Remove duplicates from our element collection
    const uniqueElements = Array.from(new Set(allPotentialElements)) as HTMLElement[];
    
    // Track processed elements to avoid duplicates
    const processedElements = new Set<HTMLElement>();
    const processedNames = new Set<string>();
    
    const elements = uniqueElements.filter(element => {
      const el = element as HTMLElement;
      
      // Skip if already processed
      if (processedElements.has(el)) return false;
      
      // Skip if this element contains another element that was already processed
      // This prevents both gc-textarea and its inner textarea from being added
      const hasProcessedChild = Array.from(processedElements).some(processed => 
        el.contains(processed) && el !== processed
      );
      if (hasProcessedChild) return false;
      
      // Skip if a parent custom component was already processed
      // Use a more generic approach - check if parent element's tag contains input keywords
      let parent = el.parentElement;
      while (parent && parent !== this.form) {
        const parentTag = parent.tagName.toLowerCase();
        if ((parentTag.includes('input') || parentTag.includes('select') || 
             parentTag.includes('textarea') || parentTag.includes('field')) &&
            processedElements.has(parent)) {
          return false;
        }
        parent = parent.parentElement;
      }
      
      // Check if element is visible
      if (el.offsetParent === null && !el.checkVisibility?.()) return false;
      // Check if not disabled
      if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') return false;
      // Check if it's actually an interactive element
      if (!this.isInteractiveElement(el)) return false;
      
      // If this is a custom component with a nested native input, prefer the custom component
      const tagName = el.tagName.toLowerCase();
      const isCustomComponent = tagName.includes('-') || // Web components have hyphens
                               el.hasAttribute('formcontrolname') ||
                               // Check if tag contains input-related keywords
                               (tagName.includes('input') || 
                                tagName.includes('select') || 
                                tagName.includes('textarea') ||
                                tagName.includes('picker') ||
                                tagName.includes('dropdown')) &&
                               !['input', 'select', 'textarea'].includes(tagName);
      
      if (isCustomComponent) {
        const nestedNativeInput = el.querySelector('input, select, textarea');
        if (nestedNativeInput) {
          processedElements.add(nestedNativeInput as HTMLElement);
        }
      }
      
      processedElements.add(el);
      return true;
    });

    const encounteredNames = new Set<string>();
    const fieldDescriptors: FieldDescriptor[] = [];

    for (const element of elements) {
      const baseNameOrId = this.getElementName(element);
      
      // Skip fields without identifiable names
      if (!baseNameOrId) {
        console.log(`[FormScanner] Skipping field without name/id:`, element);
        continue;
      }
      
      // Skip internal fields of composite components (like app-input-interval)
      if (this.isInternalFieldOfCompositeComponent(element)) {
        console.log(`[FormScanner] Skipping internal field "${baseNameOrId}" of composite component:`, element);
        continue;
      }
      
      const type = this.getElementType(element);

      // Skip if we've already processed a field with this name
      if (processedNames.has(baseNameOrId) && type !== 'radio' && type !== 'checkbox') {
        continue;
      }
      processedNames.add(baseNameOrId);
      
      console.log(`[FormScanner] Found field with name: "${baseNameOrId}"`, element);

      if (encounteredNames.has(baseNameOrId) && (type === 'radio' || type === 'checkbox' && !element.closest('.feature-options,.choice-options'))) continue;

      const required = element.hasAttribute('required') || element.getAttribute('aria-required') === 'true';
      const isPrivate = element.dataset.aiPrivate === 'true' || 
                       element.getAttribute('data-private') === 'true' ||
                       type === 'password' || 
                       /cc-|credit.*card|card.*number|cvv|cvc|exp.*date|expir/i.test(baseNameOrId) ||
                       /password|pwd|secret/i.test(baseNameOrId);
      
      // Get placeholder from multiple sources
      let placeholder: string | undefined;
      if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
        placeholder = element.placeholder;
      } else {
        placeholder = element.getAttribute('placeholder') || 
                     element.getAttribute('aria-placeholder') || 
                     undefined;
      }
      
      const value = this.extractValue(element);

      let computedLabel = this.getHumanReadableLabel(element, baseNameOrId);

      if (type === 'radio') {
        const radioGroup = elements.filter(
          el => el instanceof HTMLInputElement && el.type === 'radio' && (el as HTMLInputElement).name === baseNameOrId
        ) as HTMLInputElement[];
        
        if (radioGroup.length > 0 && !encounteredNames.has(baseNameOrId)) {
          let groupLabel = baseNameOrId; // Default to group name
          const groupLabelEl = this.findGroupLabelElement(radioGroup[0]);
          if (groupLabelEl && groupLabelEl.textContent) {
            const groupLabelText = groupLabelEl.textContent.trim();
            if (!FormScanner.isTranslationKey(groupLabelText)) {
              groupLabel = groupLabelText.replace(/\*$/, '').trim();
            }
          }
          // Individual radio options labels
          const options = radioGroup.map(rb => ({
            value: rb.value,
            label: this.getHumanReadableLabel(rb, rb.value), // Get label for individual radio
            name: rb.name
          }));

          fieldDescriptors.push({
            name: baseNameOrId,
            label: groupLabel,
            type,
            required: radioGroup.some(rb => rb.required),
            isPrivate: radioGroup.some(rb => /cc-|creditcard|cardnumber/i.test(rb.name) || rb.dataset.aiPrivate === 'true' || rb.type === 'password'),
            options,
            value: radioGroup.find(rb => rb.checked)?.value,
            element: element.hasAttribute('data-copilot-key') ? element : undefined,
            tagName: element.tagName.toLowerCase()
          });
          encounteredNames.add(baseNameOrId);
        }
      } else if (type === 'checkbox') {
        const checkboxGroupContainer = element.closest('.feature-options, .choice-options');
        if (checkboxGroupContainer && baseNameOrId) {
          const checkboxGroup = Array.from(checkboxGroupContainer.querySelectorAll<HTMLInputElement>(`input[type="checkbox"][name="${baseNameOrId}"]`));
          if (checkboxGroup.length > 0 && !encounteredNames.has(baseNameOrId)) {
            let groupLabel = baseNameOrId; // Default to group name
            const groupLabelEl = this.findGroupLabelElement(checkboxGroup[0], checkboxGroupContainer);
            if (groupLabelEl && groupLabelEl.textContent) {
                const groupLabelText = groupLabelEl.textContent.trim();
                if (!FormScanner.isTranslationKey(groupLabelText)) {
                    groupLabel = groupLabelText.replace(/\*$/, '').trim();
                }
            }
            const options = checkboxGroup.map(cb => ({
                value: cb.value || 'on',
                label: this.getHumanReadableLabel(cb, cb.value || 'on'), // Get label for individual checkbox
                name: cb.name
            }));

            fieldDescriptors.push({
              name: baseNameOrId,
              label: groupLabel,
              type: 'checkboxgroup',
              required: checkboxGroup.some(cb => cb.required),
              isPrivate: checkboxGroup.some(cb => cb.dataset.aiPrivate === 'true'),
              options,
              value: checkboxGroup.filter(cb => cb.checked).map(cb => cb.value || 'on'),
              element: element.hasAttribute('data-copilot-key') ? element : undefined,
              tagName: element.tagName.toLowerCase()
            });
            encounteredNames.add(baseNameOrId);
          }
        } else if (!encounteredNames.has(baseNameOrId)) {
          fieldDescriptors.push({
            name: baseNameOrId,
            label: computedLabel, // Use already computed label for single checkbox
            type: 'checkbox',
            required,
            isPrivate,
            value: value,
            element: element.hasAttribute('data-copilot-key') ? element : undefined,
            tagName: element.tagName.toLowerCase()
          });
          encounteredNames.add(baseNameOrId);
        }
      } else if (element.tagName.toLowerCase() === 'select') {
        const selectElement = element as HTMLSelectElement;
        const options = Array.from(selectElement.options).map(opt => ({
            value: opt.value,
            label: FormScanner.isTranslationKey(opt.text) ? opt.value : opt.text.trim() // Check option text for keys
        }));
        // Determine if we need to save element reference
        const isCustomComponent = element.tagName.toLowerCase().includes('-') || element.tagName.toLowerCase().startsWith('gc-');
        const needsElementRef = element.hasAttribute('data-copilot-key') || 
                               isCustomComponent ||
                               (!element.getAttribute('name') && !element.id && !element.getAttribute('formcontrolname'));
        
        fieldDescriptors.push({
          name: baseNameOrId,
          label: computedLabel,
          type: selectElement.type,
          required,
          isPrivate,
          placeholder,
          options,
          value: selectElement.value,
          element: needsElementRef ? element : undefined,
          tagName: element.tagName.toLowerCase()
        });
        encounteredNames.add(baseNameOrId);
      } else if (!encounteredNames.has(baseNameOrId)) { // For text, textarea, email, number etc.
        // Determine if we need to save element reference
        const isCustomComponent = element.tagName.toLowerCase().includes('-') || element.tagName.toLowerCase().startsWith('gc-');
        const needsElementRef = element.hasAttribute('data-copilot-key') || 
                               isCustomComponent ||
                               (!element.getAttribute('name') && !element.id && !element.getAttribute('formcontrolname'));
        
        fieldDescriptors.push({
          name: baseNameOrId,
          label: computedLabel,
          type,
          placeholder,
          required,
          isPrivate,
          value,
          element: needsElementRef ? element : undefined,
          tagName: element.tagName.toLowerCase()
        });
        encounteredNames.add(baseNameOrId);
      }
    }
    return fieldDescriptors;
  }

  private findLabelText(element: HTMLElement): string | null {
    // 1. Try <label for="element.id">
    if (element.id) {
      const labelElement = this.form.querySelector<HTMLLabelElement>(`label[for="${element.id}"]`);
      if (labelElement && labelElement.textContent) {
        return labelElement.textContent.trim();
      }
    }

    // 2. Try parent <label>
    let parent = element.parentElement;
    if (parent && parent.tagName.toLowerCase() === 'label' && parent.textContent) {
        // Extract text content, excluding the input/select/textarea itself
        let textContent = '';
        Array.from(parent.childNodes).forEach(node => {
          if (node !== element) { // Exclude the element itself
            textContent += node.textContent || '';
          }
        });
        return textContent.trim();
    }
    
    // 3. UNIVERSAL LABEL SEARCH - works with any framework/library
    const result = this.findUniversalLabel(element);
    if (result) return result;
    
    // 4. Try closest preceding sibling that is a common label-like tag
    const potentialLabelTags = ['P', 'SPAN', 'DIV', 'H6', 'H5', 'H4', 'H3', 'H2', 'H1', 'LABEL'];
    let sibling = element.previousElementSibling;
    if (sibling && potentialLabelTags.includes(sibling.tagName) && sibling.textContent) {
      return sibling.textContent.trim();
    }

    // If the element is wrapped (e.g., in a div), check the previous sibling of the wrapper
    const wrapper = element.parentElement;
    if (wrapper && wrapper !== this.form && wrapper !== document.body) {
        sibling = wrapper.previousElementSibling;
        if (sibling && potentialLabelTags.includes(sibling.tagName) && sibling.textContent) {
            // Check if this sibling is a label for another element to avoid conflicts
            if (sibling.tagName === 'LABEL' && (sibling as HTMLLabelElement).htmlFor && (sibling as HTMLLabelElement).htmlFor !== element.id) {
                // It's a label for something else
            } else {
                 return sibling.textContent.trim();
            }
        }
    }

    return null;
  }

  private findGroupLabelElement(firstElementInGroup: HTMLElement, groupContainer?: Element | null): Element | null {
    // 1. Fieldset + Legend
    const fieldset = firstElementInGroup.closest('fieldset');
    if (fieldset) {
      const legend = fieldset.querySelector('legend');
      if (legend) return legend;
    }

    // 2. Check specific container's previous sibling or a designated label within common parent
    const containerToProbe = groupContainer || firstElementInGroup.closest('.form-group, .radio-group, .checkbox-group, .feature-options, .choice-options');
    
    if (containerToProbe) {
        // Check for a <p>, <span>, heading, or non-specific <label> directly before the container
        let prevSibling = containerToProbe.previousElementSibling;
        if (prevSibling && (prevSibling.matches('p, span, h1,h2,h3,h4,h5,h6') || (prevSibling.tagName === 'LABEL' && !(prevSibling as HTMLLabelElement).htmlFor))) {
            return prevSibling;
        }
        // Check for a <p>, <span>, heading as the first child of the container's parent, if the container itself isn't deeply nested
        const containerParent = containerToProbe.parentElement;
        if (containerParent && containerParent !== this.form && containerParent.children[0] === containerToProbe) {
             prevSibling = containerParent.previousElementSibling;
             if (prevSibling && (prevSibling.matches('p, span, h1,h2,h3,h4,h5,h6') || (prevSibling.tagName === 'LABEL' && !(prevSibling as HTMLLabelElement).htmlFor))) {
                return prevSibling;
            }
        }
        // Look for a direct child of the container that acts as a label
        const directLabel = containerToProbe.querySelector('p, span, legend, h1,h2,h3,h4,h5,h6');
        if (directLabel && !directLabel.closest('label[for]')) { // Ensure it's not part of an individual field's label
            // Heuristic: if the directLabel contains the firstElementInGroup or its label, it's probably not the group label.
            if(!directLabel.contains(firstElementInGroup) && !Array.from(directLabel.querySelectorAll('label')).some(l => l.htmlFor === firstElementInGroup.id)) {
                 return directLabel;
            }
        }
    }
    
    // Fallback to the logic from before (simpler parent/previous sibling scan)
    // This might be redundant now or could be simplified further.
    let currentElement: Element | null = firstElementInGroup.parentElement;
    while(currentElement && currentElement !== this.form) {
        let previousSibling = currentElement.previousElementSibling;
        while(previousSibling) {
            if (previousSibling.matches('p, span, h1,h2,h3,h4,h5,h6,legend') || (previousSibling.tagName === 'LABEL' && !(previousSibling as HTMLLabelElement).htmlFor)) {
                return previousSibling;
            }
            previousSibling = previousSibling.previousElementSibling;
        }
        currentElement = currentElement.parentElement;
    }
    return null;
  }
  
  private findUniversalLabel(element: HTMLElement): string | null {
    // Universal label search that works with any framework/library
    let current = element;
    let searchDepth = 0;
    const maxDepth = 5;
    
    while (current && searchDepth < maxDepth) {
      const parent = current.parentElement;
      if (!parent) break;
      
      // 1. Look for universal label-like elements in the same container
      const labelSelectors = [
        // Standard labels
        'label',
        // Framework-specific labels (Angular, React, Vue, etc.)
        '*[class*="label"]',
        '*[class*="Label"]',
        // ARIA labels
        '*[role="label"]',
        // Common headings/text elements that act as labels
        'h1, h2, h3, h4, h5, h6',
        'p, span, div',
        // Framework-specific components
        'gc-label, mat-label, v-label, form-label',
        // Any element with label-like attributes
        '*[aria-label]',
        '*[title]'
      ];
      
      for (const selector of labelSelectors) {
        // Look for label BEFORE the current element (common pattern)
        const labelElement = this.findLabelInContainer(parent, selector, current);
        if (labelElement) {
          const labelText = this.extractCleanLabelText(labelElement);
          if (labelText && !FormScanner.isTranslationKey(labelText)) {
            return labelText;
          }
        }
      }
      
      current = parent;
      searchDepth++;
    }
    
    return null;
  }
  
  private findLabelInContainer(container: HTMLElement, selector: string, targetElement: HTMLElement): HTMLElement | null {
    try {
      const elements = container.querySelectorAll(selector);
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLElement;
        // Check if this label is positioned before our target element
        if (this.isElementBefore(el, targetElement) && el.textContent?.trim()) {
          return el;
        }
      }
    } catch (e) {
      // Invalid selector, skip
    }
    return null;
  }
  
  private isElementBefore(labelElement: HTMLElement, targetElement: HTMLElement): boolean {
    // Check if label comes before target in DOM order
    const position = labelElement.compareDocumentPosition(targetElement);
    return (position & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
  }
  
  private extractCleanLabelText(element: HTMLElement): string | null {
    if (!element.textContent) return null;
    
    // Extract text and clean it
    let text = element.textContent.trim();
    
    // Remove common label suffixes/prefixes
    text = text.replace(/^(Label|label):\s*/i, '');
    text = text.replace(/\s*:$/, '');
    text = text.replace(/\s*\*\s*$/, ''); // Remove required asterisk
    text = text.replace(/\s*\(.*?\)\s*$/, ''); // Remove parenthetical text like "(optional)"
    
    return text.trim() || null;
  }
} 